import { memo, useEffect, useMemo, useRef } from 'react'
import { Radio } from '~components/ui/control/radio'
import { cn } from '~utils/cn'
import { GroupItem } from '../../types'
import { getGroupItemDisplayValue } from '../../utils'
import ExtraSelectBox from './extra-select-box'
import ExtraTextInput from './extra-text-input'

interface RadioSelectProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  root: any
  item: GroupItem
  isSelected: boolean
  onSelect: (triggeredItem: GroupItem & { passive?: boolean }) => void
}

const RadioSelect = memo(({ root, item, isSelected, onSelect }: RadioSelectProps) => {
  const initialized = useRef(false)
  const { extra_component } = item
  const displayValue = useMemo(() => getGroupItemDisplayValue(item), [item])

  const renderExtraComponent = useMemo(() => {
    if (!extra_component) {
      return null
    }
    const { type } = extra_component
    switch (type) {
      case 'select': {
        const { datafield, placeholder, options } = extra_component

        return (
          <ExtraSelectBox
            root={root}
            placeholder={placeholder}
            datafield={datafield}
            options={options}
            onSelect={event => onSelect(event)}
          />
        )
      }
      case 'text': {
        const { datafield, placeholder } = extra_component

        return (
          <ExtraTextInput
            root={root}
            placeholder={placeholder}
            datafield={datafield}
            onChange={event => {
              onSelect(event)
            }}
          />
        )
      }
      default:
        return null
    }
  }, [root, extra_component, onSelect])

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true
      if (!extra_component) {
        return
      }

      const { type } = extra_component

      if (root[extra_component.datafield] === undefined) {
        switch (type) {
          case 'select':
            {
              const { datafield } = extra_component
              onSelect({ datafield, value: null, extra_component, passive: true })
            }
            break
          case 'text':
            {
              const { datafield } = extra_component
              onSelect({ datafield, value: null, extra_component, passive: true })
            }
            break
        }
      }
    }
  }, [extra_component, onSelect, root])

  return (
    <button
      type="button"
      className={cn(
        'w-full flex flex-col rounded-[8px] px-4 py-3 transition-all duration-300',
        isSelected && 'bg-white dark:bg-gray-800',
        !isSelected && 'bg-transparent',
        isSelected && renderExtraComponent && 'flex-gap-y-3'
      )}
      onClick={() => onSelect(item)}
    >
      <div className="w-full flex-row flex items-center flex-gap-x-4">
        <Radio
          className="transition-colors duration-300"
          id={displayValue}
          checked={isSelected}
          readOnly
        />
        <span
          className={cn(
            'text-start text-body-1 text-gray-900 dark:text-gray-500',
            isSelected && 'dark:text-white'
          )}
        >
          {displayValue}
        </span>
      </div>
      {isSelected && renderExtraComponent}
    </button>
  )
})

RadioSelect.displayName = 'RadioSelect'

export default RadioSelect
